import type { ButtonProps } from '../Button/const';
import type { ModalAnimateType, ModalProps } from './const';
import * as Dialog from '@radix-ui/react-dialog';

import { cn } from '@utils';
import { IconClose } from '@wegic/icon';
import * as React from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import Button from '../Button';
import Divider from '../Divider';
import './index.less';

function Modal(props: ModalProps, ref: React.Ref<HTMLDivElement>) {
  const {
    id,
    children,
    style,
    className,
    containerClassName,
    closeClassName,
    headerClassName,
    footerClassName,
    onCancel,
    onOk,
    title,
    visible: visibleProps,
    okText = 'Ok',
    cancelText = 'Cancel',
    okButtonProps,
    cancelButtonProps,
    closable = true,
    closeIcon,
    confirmLoading,
    hideFooter,
    hideFooterBorder = false,
    hideCancel,
    description,
    hiddenHeaderDesc = false,
    dialogContentProps,
    maskStyle,
    maskIsClose = true,
    onClose,
    isPointerDownClose = false,
    disableOnInteractOutside = false,
    customRenderContent,
    onOverlayClick,
    onClickModal,
    otherContent,
    priority,
    size,
  } = props;

  const [isOpen = false, setIsOpen] = useState<boolean>(false);
  const [animate, setAnimate] = useState<ModalAnimateType>('appear');
  const isAppearAnimate = animate === 'appear';
  const timer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (visibleProps) {
      setIsOpen(true);
      setAnimate('appear');
    } else {
      setAnimate('disappear');
      timer.current = setTimeout(() => {
        setIsOpen(false);
      }, 75); // 关闭动画的时间
    }
    return () => {
      clearTimeout(timer.current);
    };
  }, [visibleProps]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOverlayClick && onOverlayClick();
  };

  const handleClickModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClickModal && onClickModal();
  };

  const handleClose = (isMask: boolean = false) => {
    if (isMask && !maskIsClose) {
      return;
    }
    if (onClose && !isMask) {
      onClose();
    } else {
      onCancel?.();
    }
  };

  const CloseIcon = () => {
    if (closeIcon) {
      return <Dialog.Close asChild>{closeIcon}</Dialog.Close>;
    }

    return (
      <Dialog.Close asChild>
        <Button
          type="secondary"
          size="small"
          icon={<IconClose fontSize={16} />}
          style={{ width: '40px' }}
          className="wg-modal__dialog-btn"
          onMouseDown={e => {
            e.preventDefault();
          }}
        />
      </Dialog.Close>
    );
  };

  const Footer = () => {
    return (
      <div className="wg-modal__footer-button-group">
        {!hideCancel && (
          <Button
            className="wg-modal__footer-button"
            {...(cancelButtonProps as ButtonProps)}
            type="secondary"
            onClick={(e: React.MouseEvent) => onCancel?.(e)}
          >
            {cancelText}
          </Button>
        )}
        <Button
          className="wg-modal__footer-button"
          {...(okButtonProps as ButtonProps)}
          type="primary"
          onClick={(e: React.MouseEvent) => onOk?.(e)}
          loading={okButtonProps?.loading || confirmLoading}
        >
          {okText}
        </Button>
      </div>
    );
  };

  const renderContent = () => {
    const content = (
      <Dialog.Content
        id={id}
        ref={ref}
        style={{ ...style }}
        className={cn(
          'wg-modal',
          {
            'wg-modal--animate-appear': isAppearAnimate,
            'wg-modal--animate-disappear': !isAppearAnimate,
            [`wg-modal--size-${size}`]: !!size,
          },
          className,
        )}
        onInteractOutside={event => {
          // 如果isPointerDownClose为false,则触发onInteractOutside函数的关闭事件
          !disableOnInteractOutside && !isPointerDownClose && handleClose(true);
          event.preventDefault();
        }}
        onPointerDownOutside={event => {
          // 鼠标点击到外部区域关闭
          isPointerDownClose && handleClose(true);
          event.preventDefault();
        }}
        {...dialogContentProps}
      >
        {title ? (
          <div className={cn('wg-modal__header-bar', headerClassName)}>
            <Dialog.Title className="wg-modal__title">{title}</Dialog.Title>
          </div>
        ) : (
          <Dialog.Title></Dialog.Title>
        )}
        {!hiddenHeaderDesc && description && (
          <Dialog.Description className="wg-modal__description">{description}</Dialog.Description>
        )}
        {children && <div className="wg-modal__content">{children}</div>}
        {!hideFooter && (
          <div className={cn('wg-modal__footer', footerClassName)}>
            {!hideFooterBorder && <Divider plain />}
            <Footer />
          </div>
        )}
        <div className={cn('wg-modal__close-container', closeClassName)}>{closable && <CloseIcon />}</div>
      </Dialog.Content>
    );

    return customRenderContent ? customRenderContent(content) : content;
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={open => !open && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="wg-modal__overlay" style={maskStyle} onClick={handleOverlayClick} />
        <div
          onClick={handleClickModal}
          className={cn('wg-modal__dialog-content-container', containerClassName, {
            'wg-modal__dialog-content-container--high': priority === 'high',
            'wg-modal__dialog-content-container--low': priority === 'low',
          })}
        >
          {renderContent()}
          {otherContent ?? null}
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const ModalComponent = forwardRef<HTMLDivElement, ModalProps>(Modal);

ModalComponent.displayName = 'Modal';

export default ModalComponent;

export type { ModalAnimateType, ModalPriority, ModalProps, ModalSize } from './const';
